/**
 * Yazdırma kuyruğu / geçmişi (Tb_Rn_Sys_DevicePrintJobs): hangi iş hangi cihaza gitti, bekliyor
 * mu, neden başarısız. Bekleyen iş iptal edilebilir (cihaz bağlanınca basılmasın diye).
 */
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, X } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { queryKeys } from '@/shared/api/queryKeys';
import { printApi, type PrintJobDto } from '@/shared/api/printApi';
import { cn } from '@/shared/lib/cn';

const STATE_CLASS: Record<PrintJobDto['state'], string> = {
  Queued: 'bg-warning/15 text-warning',
  Sent: 'bg-brand-500/10 text-brand-600',
  Done: 'bg-success/15 text-success',
  Failed: 'bg-danger/15 text-danger',
  Expired: 'bg-bg-muted text-fg-muted',
};

export function PrintJobsPanel({ open, isSys }: { open: boolean; isSys: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: queryKeys.device.printJobs(),
    queryFn: () => printApi.jobs({ take: 30 }),
    enabled: open,
    refetchInterval: open ? 10_000 : false,
  });
  const cancel = useMutation({
    mutationFn: (jobId: string) => printApi.cancelJob(jobId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.device.printJobs() }),
  });
  const rows = jobs.data ?? [];
  const pending = rows.filter((j) => j.state === 'Queued').length;

  return (
    <div className="flex flex-col gap-1">
      <div className="text-fg-muted flex items-center gap-1 text-xs font-medium">
        <Clock className="h-3.5 w-3.5" />
        {t('shell.user.devicesJobs')}
        {pending > 0 ? <span className="bg-warning/15 text-warning rounded-full px-1.5">{pending}</span> : null}
      </div>
      {rows.length === 0 ? (
        <div className="text-fg-subtle px-1 py-2 text-xs">{t('shell.user.devicesJobsEmpty')}</div>
      ) : (
        <ul className="divide-border border-border max-h-56 divide-y overflow-y-auto rounded-md border text-xs" data-testid="print-jobs">
          {rows.map((j) => (
            <li key={j.devicePrintJobId} className="flex items-center gap-2 px-2 py-1">
              <span className={cn('shrink-0 rounded px-1.5 py-0.5', STATE_CLASS[j.state] ?? STATE_CLASS.Expired)}>
                {t(`shell.user.devicesJobState.${j.state}`, { defaultValue: j.state })}
              </span>
              <span className="min-w-0 flex-1 truncate" title={j.lastError ?? undefined}>
                <strong>{j.deviceCode}</strong>/{j.printerName}
                {j.title ? ` · ${j.title}` : ''}
                {j.copies > 1 ? ` ×${j.copies}` : ''}
                {j.lastError ? <span className="text-danger"> — {j.lastError}</span> : null}
              </span>
              <span className="text-fg-subtle shrink-0">
                {j.source === 'Worker' ? 'W · ' : ''}
                {new Date(j.addDate).toLocaleString()}
              </span>
              {isSys && j.state === 'Queued' ? (
                <Button variant="ghost" size="sm" onClick={() => cancel.mutate(j.devicePrintJobId)} title={t('shell.user.devicesJobCancel')}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
